/**
 * shared/schema/privacy.ts
 *
 * Task #1704 — Share Spaces: Privacy Disclosures, Consent Ledger & DSAR.
 *
 * Persistence substrate for:
 *   - `privacy_policy_versions`     — SA-managed regional/locale policy
 *                                     templates. Every consent row pins a
 *                                     specific version so the legal text the
 *                                     viewer accepted is always recoverable.
 *   - `viewer_consents`             — every cookie / NDA / form-submission
 *                                     consent recorded for a public viewer of
 *                                     a share space. Rows are immutable and
 *                                     HMAC-signed (defence in depth).
 *   - `worker_share_consents`       — per-worker, per-space grant/revoke of
 *                                     consent to appear in a share space.
 *                                     Append-only — a "revoke" inserts a new
 *                                     row instead of mutating the prior grant.
 *   - `dsar_requests`               — viewer/worker access/portability/deletion/
 *                                     rectification requests, with SLA timer.
 *   - `dsar_request_artifacts`      — DPO-collated artifacts (data dump,
 *                                     audit extract, redacted bundle).
 *   - `dsar_request_events`         — append-only state-change ledger for a
 *                                     DSAR (submitted → verified → fulfilled
 *                                     → closed | rejected).
 *
 * Discipline:
 *   - Tenant scoping is denormalised onto every row so the existing tenant
 *     isolation middleware can filter naturally; the public DSAR submit
 *     endpoint resolves the tenant from the share-space token first and
 *     stamps it on the row.
 *   - HMAC signatures live in a `signature` column on the immutable ledger
 *     tables (`viewer_consents`, `worker_share_consents`) so a DB compromise
 *     that rewrites a row is detectable on a re-verify pass.
 *   - We deliberately do NOT store raw IP addresses or full UAs — we keep
 *     a 32-char SHA-256 prefix to support uniqueness checks while making
 *     mass viewer re-identification much harder if the table is leaked.
 */

import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  integer,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants, users } from "./common";

// ─── Enums / constants ────────────────────────────────────────────────

export const PRIVACY_POLICY_KINDS = [
  "share_viewer_notice",
  "share_cookie_banner",
  "share_nda",
  "worker_consent",
] as const;
export type PrivacyPolicyKind = (typeof PRIVACY_POLICY_KINDS)[number];

export const VIEWER_CONSENT_TYPES = [
  "cookie_banner",
  "nda",
  "form_submission",
  "lead_capture",
] as const;
export type ViewerConsentType = (typeof VIEWER_CONSENT_TYPES)[number];

export const WORKER_SHARE_CONSENT_ACTIONS = ["granted", "revoked", "tombstoned"] as const;
export type WorkerShareConsentAction = (typeof WORKER_SHARE_CONSENT_ACTIONS)[number];

export const DSAR_SUBJECT_TYPES = ["viewer", "worker"] as const;
export type DsarSubjectType = (typeof DSAR_SUBJECT_TYPES)[number];

export const DSAR_REQUEST_TYPES = [
  "access",
  "portability",
  "deletion",
  "rectification",
] as const;
export type DsarRequestType = (typeof DSAR_REQUEST_TYPES)[number];

export const DSAR_STATUSES = [
  "submitted",
  "verified",
  "in_progress",
  "fulfilled",
  "rejected",
  "closed",
] as const;
export type DsarStatus = (typeof DSAR_STATUSES)[number];

export const DSAR_ARTIFACT_KINDS = [
  "viewer_events",
  "worker_data",
  "consent_records",
  "redaction_summary",
  "bundle",
] as const;
export type DsarArtifactKind = (typeof DSAR_ARTIFACT_KINDS)[number];

// ─── Tables ───────────────────────────────────────────────────────────

export const privacyPolicyVersions = pgTable(
  "privacy_policy_versions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // null tenant = SA-managed default template applicable to all tenants
    tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(),
    region: text("region").notNull().default("global"),
    locale: text("locale").notNull().default("en"),
    version: integer("version").notNull().default(1),
    title: text("title").notNull(),
    body: text("body").notNull(),
    dataController: jsonb("data_controller")
      .$type<{ name: string; email: string; address?: string | null }>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    retentionDays: integer("retention_days").notNull().default(365),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byScope: index("privacy_policy_versions_scope_idx").on(
      t.tenantId,
      t.kind,
      t.region,
      t.locale,
    ),
    uniqVersion: uniqueIndex("privacy_policy_versions_unique").on(
      t.tenantId,
      t.kind,
      t.region,
      t.locale,
      t.version,
    ),
  }),
);

export const viewerConsents = pgTable(
  "viewer_consents",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id"),
    tokenId: text("token_id"),
    consentType: text("consent_type").notNull(),
    viewerEmailHash: text("viewer_email_hash"),
    viewerIdentifier: text("viewer_identifier"),
    policyVersionId: text("policy_version_id").references(() => privacyPolicyVersions.id, {
      onDelete: "set null",
    }),
    locale: text("locale").notNull().default("en"),
    ipHash: text("ip_hash"),
    userAgentHash: text("user_agent_hash"),
    granted: text("granted").notNull().default("true"),
    signature: text("signature"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byTenant: index("viewer_consents_tenant_idx").on(t.tenantId, t.occurredAt),
    bySpace: index("viewer_consents_space_idx").on(t.spaceId, t.occurredAt),
    byEmail: index("viewer_consents_email_idx").on(t.tenantId, t.viewerEmailHash),
  }),
);

export const workerShareConsents = pgTable(
  "worker_share_consents",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    workerId: text("worker_id").notNull(),
    spaceId: text("space_id"),
    action: text("action").notNull(),
    policyVersionId: text("policy_version_id").references(() => privacyPolicyVersions.id, {
      onDelete: "set null",
    }),
    actorUserId: text("actor_user_id").references(() => users.id, { onDelete: "set null" }),
    reason: text("reason"),
    signature: text("signature"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byTenantWorker: index("worker_share_consents_worker_idx").on(t.tenantId, t.workerId),
    bySpace: index("worker_share_consents_space_idx").on(t.spaceId, t.occurredAt),
  }),
);

export const dsarRequests = pgTable(
  "dsar_requests",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    spaceId: text("space_id"),
    subjectType: text("subject_type").notNull(),
    requestType: text("request_type").notNull(),
    status: text("status").notNull().default("submitted"),
    contactEmail: text("contact_email").notNull(),
    contactEmailHash: text("contact_email_hash").notNull(),
    subjectName: text("subject_name"),
    subjectIdentifier: text("subject_identifier"),
    description: text("description"),
    locale: text("locale").notNull().default("en"),
    region: text("region").notNull().default("global"),
    policyVersionId: text("policy_version_id").references(() => privacyPolicyVersions.id, {
      onDelete: "set null",
    }),
    verificationTokenHash: text("verification_token_hash"),
    verifiedAt: timestamp("verified_at", { withTimezone: true }),
    slaDueAt: timestamp("sla_due_at", { withTimezone: true }).notNull(),
    fulfilledAt: timestamp("fulfilled_at", { withTimezone: true }),
    fulfilledByUserId: text("fulfilled_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    rejectionReason: text("rejection_reason"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
    submittedAt: timestamp("submitted_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byTenantStatus: index("dsar_requests_tenant_status_idx").on(t.tenantId, t.status, t.slaDueAt),
    byEmail: index("dsar_requests_email_idx").on(t.tenantId, t.contactEmailHash),
  }),
);

export const dsarRequestArtifacts = pgTable(
  "dsar_request_artifacts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    requestId: text("request_id")
      .notNull()
      .references(() => dsarRequests.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(),
    payload: jsonb("payload").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
    sha256: text("sha256").notNull(),
    sizeBytes: integer("size_bytes").notNull().default(0),
    redacted: text("redacted").notNull().default("false"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byRequest: index("dsar_request_artifacts_req_idx").on(t.requestId, t.kind),
  }),
);

export const dsarRequestEvents = pgTable(
  "dsar_request_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id, { onDelete: "cascade" }),
    requestId: text("request_id")
      .notNull()
      .references(() => dsarRequests.id, { onDelete: "cascade" }),
    fromStatus: text("from_status"),
    toStatus: text("to_status").notNull(),
    actorUserId: text("actor_user_id").references(() => users.id, { onDelete: "set null" }),
    actorRole: text("actor_role"),
    note: text("note"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
    occurredAt: timestamp("occurred_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    byRequest: index("dsar_request_events_req_idx").on(t.requestId, t.occurredAt),
  }),
);

// ─── Insert schemas + types ───────────────────────────────────────────

export const dataControllerSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email(),
  address: z.string().max(500).nullable().optional(),
});
export type DataController = z.infer<typeof dataControllerSchema>;

export const insertPrivacyPolicyVersionSchema = createInsertSchema(privacyPolicyVersions, {
  dataController: dataControllerSchema,
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPrivacyPolicyVersion = z.infer<typeof insertPrivacyPolicyVersionSchema>;
export type PrivacyPolicyVersion = typeof privacyPolicyVersions.$inferSelect;

export const insertViewerConsentSchema = createInsertSchema(viewerConsents).omit({
  id: true,
  occurredAt: true,
});
export type InsertViewerConsent = z.infer<typeof insertViewerConsentSchema>;
export type ViewerConsent = typeof viewerConsents.$inferSelect;

export const insertWorkerShareConsentSchema = createInsertSchema(workerShareConsents).omit({
  id: true,
  occurredAt: true,
});
export type InsertWorkerShareConsent = z.infer<typeof insertWorkerShareConsentSchema>;
export type WorkerShareConsent = typeof workerShareConsents.$inferSelect;

export const insertDsarRequestSchema = createInsertSchema(dsarRequests).omit({
  id: true,
  submittedAt: true,
  updatedAt: true,
});
export type InsertDsarRequest = z.infer<typeof insertDsarRequestSchema>;
export type DsarRequest = typeof dsarRequests.$inferSelect;

export const insertDsarArtifactSchema = createInsertSchema(dsarRequestArtifacts).omit({
  id: true,
  createdAt: true,
});
export type InsertDsarArtifact = z.infer<typeof insertDsarArtifactSchema>;
export type DsarRequestArtifact = typeof dsarRequestArtifacts.$inferSelect;

export const insertDsarEventSchema = createInsertSchema(dsarRequestEvents).omit({
  id: true,
  occurredAt: true,
});
export type InsertDsarEvent = z.infer<typeof insertDsarEventSchema>;
export type DsarRequestEvent = typeof dsarRequestEvents.$inferSelect;

// ─── Public submission Zod schema (route bodies) ──────────────────────

export const publicDsarSubmitSchema = z.object({
  subjectType: z.enum(DSAR_SUBJECT_TYPES),
  requestType: z.enum(DSAR_REQUEST_TYPES),
  contactEmail: z.string().email().max(320),
  subjectName: z.string().trim().max(200).optional(),
  subjectIdentifier: z.string().trim().max(200).optional(),
  description: z.string().trim().max(4000).optional(),
  locale: z.string().trim().max(16).optional(),
  region: z.string().trim().max(16).optional(),
  acknowledgedPolicyVersionId: z.string().trim().max(64).optional(),
});
export type PublicDsarSubmitBody = z.infer<typeof publicDsarSubmitSchema>;

export const publicViewerConsentSchema = z.object({
  consentType: z.enum(VIEWER_CONSENT_TYPES),
  granted: z.boolean(),
  policyVersionId: z.string().trim().max(64).optional(),
  locale: z.string().trim().max(16).optional(),
  email: z.string().email().max(320).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type PublicViewerConsentBody = z.infer<typeof publicViewerConsentSchema>;

export const DEFAULT_DSAR_SLA_HOURS = 30 * 24; // 30 days
